import Linkify from 'linkify-react';
import React, { useEffect, useRef, useState } from 'react';
import { BiCheck } from 'react-icons/bi';
import { CgClose } from 'react-icons/cg';
import { MdVerified } from 'react-icons/md';
import styled, { css, keyframes } from 'styled-components';

import useApi from '../../actions/useApi';
import { useAppContext } from '../../appContext';
import { closeUserForm, selectComment } from '../../store/actions';
import { useStateContext } from '../../store/stateContext';
import { Comment, Reply, User } from '../../types';
import { formatName } from '../../utils';
import Avatar from '../avatar';
import CommentCard from '../comment';
import CommentInput from '../comment-input';
import Footer from '../footer';

// Add keyframes for animations if not already defined
const slideInLeftAnimation = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideInRightAnimation = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideInAnimation = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Styled components
const Container = styled.div<{ widgetAlignment?: string }>`
  display: flex;
  flex-direction: column;
  gap: 8px !important; /* 0.5rem = 8px */
  width: 100%;
  max-width: 100%;

  @media (min-width: 768px) {
    width: 360px !important;
    max-width: 360px !important;
  }

  ${props => {
    switch (props.widgetAlignment) {
      case 'leftcenter':
        return css`animation: ${slideInLeftAnimation} 0.2s ease-out forwards !important;`;
      case 'rightcenter':
        return css`animation: ${slideInRightAnimation} 0.2s ease-out forwards !important;`;
      default:
        return css`animation: ${slideInAnimation} 0.2s ease-out forwards !important;`;
    }
  }}
`;

const CloseButton = styled.button<{ isDarkMode: boolean }>`
  border: none;
  background-color: ${(props) => props.isDarkMode ? 'white' : '#585568'} !important;
  color: ${(props) => props.isDarkMode ? 'black' : 'white'} !important;
  height: 32px !important;
  font-size: 12px !important;
  padding: 8px !important; /* 0.5rem = 8px */
  border-radius: 24px !important; /* 1.5rem = 24px */
  display: flex;
  flex-direction: row;
  gap: 4px !important; /* 0.25rem = 4px */
  padding-left: 16px !important; /* 1rem = 16px */
  padding-right: 16px !important; /* 1rem = 16px */
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  margin-right: 4px !important; /* 0.25rem = 4px */
  &:hover {
    background-color: ${(props) => props.isDarkMode ? 'white' : '#585568'} !important;
    color: ${(props) => props.isDarkMode ? 'black' : 'white'} !important;
  }

  @media (min-width: 768px) {
    margin-right: 0 !important;
  }
`;

const CloseIcon = styled(CgClose)`
  height: 16px;
  width: 16px;
`;

const CloseText = styled.p<{ isDarkMode: boolean }>`
  color: ${(props) => props.isDarkMode ? 'black' : 'white'};
  margin: 0;
  line-height: 20px;
`;

const CommentContainer = styled.div<{ isDarkMode: boolean; widgetAlignment?: string }>`
  width: 100%;
  background-color: ${(props) => props.isDarkMode ? 'white' : '#565366'} !important;
  color: ${(props) => props.isDarkMode ? 'black' : 'white'} !important;
  border-radius: 16px !important; /* 1rem = 16px */

  ${props => props.widgetAlignment === 'leftbottom' && css`
    border-radius: 16px 16px 0 0 !important; /* 1rem = 16px */
  `}
  ${props => props.widgetAlignment === 'rightbottom' && css`
    border-radius: 16px 16px 0 0 !important; /* 1rem = 16px */
  `}
  ${props => props.widgetAlignment === 'leftcenter' && css`
    border-radius: 0 16px 16px 0 !important; /* 1rem = 16px */
  `}
  ${props => props.widgetAlignment === 'rightcenter' && css`
    border-radius: 16px 0 0 16px !important; /* 1rem = 16px */
  `}

  @media (min-width: 768px) {
    ${props => props.widgetAlignment === 'leftcenter' || props.widgetAlignment === 'rightcenter' ? css`
      ${props.widgetAlignment === 'leftcenter' && css`
        border-radius: 0 16px 16px 0 !important;
      `}
      ${props.widgetAlignment === 'rightcenter' && css`
        border-radius: 16px 0 0 16px !important; 
      `}
    ` : css`
      border-radius: 16px !important; 
    `}
  }

  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  display: flex;
  flex-direction: column;
  
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 0 16px !important; /* 1rem = 16px */
`;

const RepliesContainer = styled.div<{ isUserFormOpen: boolean; repliesCount: number }>`
  flex: 1;
  margin-bottom: ${(props) => (props.isUserFormOpen && props.repliesCount < 4 ? '200px' : '0px')};
  overflow-y: auto;
`;

const RepliesList = styled.div<{ isEmpty: boolean }>`
  padding-left: ${(props) => props.isEmpty ? '0' : '32px'} !important; /* 2rem = 32px */
  max-height: 220px !important;
  min-height: 30px !important;
`;

const ReplyInputContainer = styled.div`
  padding: 0 16px !important; /* 1rem = 16px */
  padding-top: 8px !important; /* 0.5rem = 8px */
  padding-bottom: 0px !important;
`;

const SuccessMessage = styled.div`
  border-radius: 24px !important; /* 1.5rem = 24px */
  background-color: #23C68D !important;
  padding: 8px 16px !important; /* 0.5rem = 8px, 1rem = 16px */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const SuccessText = styled.p`
  color: white;
  margin: 0;
`;

export const SuccessIcon = styled(BiCheck)`
  font-size: 20px !important; /* 1.25rem = 20px */
  font-weight: bold;
`;

const FooterContainer = styled.div`
  padding-bottom: 10px !important; /* 0.5rem = 8px */
`;

const FooterWrapper = styled.div`
margin-top: 10px !important; /* 0.4rem = 6.4px */
`;

// Reply styled components
const ReplyWrapper = styled.div<{ isDarkMode: boolean }>`
  color: ${(props) => props.isDarkMode ? '#222222' : 'white'} !important;
  border-radius: 16px !important; /* 1rem = 16px */
  padding-right: 8px !important; /* 0.5rem = 8px */
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 16px !important; /* 1rem = 16px */
  padding-top: 5px !important;
`;

const AvatarContainer = styled.div`
  width: 32px; /* 2rem = 32px */
`;

export const FaviconImage = styled.img<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 9999px;
`;

const ReplyContent = styled.div`
  width: 100%;
  padding-right: 32px; /* 2rem = 32px */
`;

const ReplyAuthor = styled.p<{ isDarkMode: boolean }>`
  all: revert;
  display: flex;
  gap: 4px !important; /* 0.25rem = 4px */
  align-items: center;
  font-size: 12px !important;
  line-height: 16px !important;
  letter-spacing: 0px !important;
  font-weight: 700;
  color: ${(props) => props.isDarkMode ? 'black' : '#999999'};
  margin: 0 !important;
`;

const VerifiedIcon = styled(MdVerified) <{ isDarkMode: boolean }>`
  color: ${(props) => props.isDarkMode ? '#5942D1' : '#23C68D'};
`;

const ReplyMessage = styled.p<{ isDarkMode: boolean }>`
  all: revert;
  font-size: 14px !important; /* 0.875rem = 14px */
  line-height: 20px !important;
  letter-spacing: 0px !important;
  font-weight: 400;
  padding-bottom: 12px !important; /* 0.75rem = 12px */
  color: ${(props) => props.isDarkMode ? 'black' : 'white'} !important;
  word-wrap: normal !important;
  word-break: break-word !important;
  margin: 0 !important;
`;

const StyledLink = styled.a<{ isDarkMode: boolean }>`
  text-decoration: underline;
  color: ${(props) => props.isDarkMode ? '#5942D1' : '#23C68D'};
  
  &:hover {
    background-color: ${(props) => props.isDarkMode ? '#f1f5f9' : 'transparent'} !important;
  }
`;

const SelectedComment: React.FC = () => {
  const { addReply } = useApi();
  const replyRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { state, dispatch } = useStateContext();
  const [success, setSuccess] = React.useState(false);
  const { widgetAlignment, openCommentsList } = useAppContext();
  const comment: Comment = state.data.data.find((c: Comment) => c.id === state.selectedCommentId) as Comment;
  const repliesEndRef = useRef<HTMLDivElement>(null); // Reference for the end of the replies
  const [userAddedReply, setUserAddedReply] = useState(false);
  const params = new URLSearchParams(window.location.search);

  const replyId = params.get('reply_id') as string;
  const handleClose = () => {
    dispatch(selectComment({ commentId: '' }))
    openCommentsList();
    dispatch(closeUserForm());
  }
  useEffect(() => {
    const scrollToReply = (id: string) => {
      if (replyRefs.current[id]) {
        replyRefs.current[id].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };
    scrollToReply(replyId);
  }, [replyId])
  const isDarkMode = state.themeMode === 'dark';

  // Function to scroll to the bottom of the replies
  const scrollToBottom = () => {
    if (repliesEndRef.current) {
      repliesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (userAddedReply) {
      scrollToBottom(); // Scroll to bottom whenever new replies are added
    }
  }, [comment?.replies?.length, userAddedReply]); // Run this effect when replies length changes

  const handleSubmit = async (message: string, values: Partial<User> | undefined) => {
    setUserAddedReply(true);
    await addReply(`${state.productInfo?.id}`, message, comment.id, values);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  }

  return (
    <Container widgetAlignment={widgetAlignment}>
      <CloseButton isDarkMode={isDarkMode} onClick={handleClose}>
        <CloseIcon />
        <CloseText isDarkMode={isDarkMode}>Close</CloseText>
      </CloseButton>
      <CommentContainer isDarkMode={isDarkMode} widgetAlignment={widgetAlignment}>
        <CommentHeader>
          <CommentCard fullWidth={true} comment={comment} background={isDarkMode ? '#EDF0F1' : '#625F74'} shadow={false} openMode />
        </CommentHeader>
        <RepliesContainer isUserFormOpen={state.userForm.isOpen} repliesCount={comment?.replies?.length || 0}>
          <RepliesList isEmpty={!comment?.replies?.length}>
            {comment?.replies?.map(reply => (
              <CommentReply key={reply.id} reply={reply} replyRefs={replyRefs} />
            ))}
            {/* Empty div used as the scrolling target */}
            <div ref={repliesEndRef} />
          </RepliesList>
        </RepliesContainer>
        {
          comment.approved !== null && (
            <ReplyInputContainer>
              {success ? (
                <SuccessMessage>
                  <SuccessText>Reply posted!</SuccessText>
                  <SuccessIcon />
                </SuccessMessage>
              ) : (
                <CommentInput
                  placeholder='Reply'
                  commentId={comment.id}
                  onSubmit={handleSubmit}
                  label="Post my reply"
                />
              )}
            </ReplyInputContainer>
          )
        }
        <FooterContainer>
          <FooterWrapper>
            <Footer isDarkMode={isDarkMode} />
          </FooterWrapper>
        </FooterContainer>
      </CommentContainer>
    </Container>
  );
};

interface IReplyProps {
  reply: Reply;
  replyRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
}

const CommentReply: React.FC<IReplyProps> = ({ reply, replyRefs }) => {
  const { state } = useStateContext();
  const isDarkMode = state.themeMode === 'dark';
  const { user: { name, image, role_id }, message } = reply;

  const componentDecorator = (href: string, text: string, key: number) => (
    <StyledLink
      href={href}
      key={key}
      target="_blank"
      rel="noopener noreferrer"
      isDarkMode={isDarkMode}
    >
      {text.slice(0, 25)}...
    </StyledLink>
  );

  return (
    <ReplyWrapper
      ref={(el) => (replyRefs.current[reply.id] = el)}
      isDarkMode={isDarkMode}
    >
      <AvatarContainer>
        {role_id === 3 ? (
          <FaviconImage
            alt="favicon"
            src={`https://www.google.com/s2/favicons?domain=https://${window.location.hostname}&sz=64`}
            size={32}
          />
        ) : (
          <Avatar url={image} name={name} size={32} />
        )}
      </AvatarContainer>
      <ReplyContent>
        <ReplyAuthor isDarkMode={isDarkMode}>
          {formatName(name)}
          {role_id === 3 && <VerifiedIcon isDarkMode={isDarkMode} />}
        </ReplyAuthor>
        <Linkify componentDecorator={componentDecorator}>
          <ReplyMessage isDarkMode={isDarkMode}>{message}</ReplyMessage>
        </Linkify>
      </ReplyContent>
    </ReplyWrapper>
  );
};

export default SelectedComment;
